/**
 * Shared URL builder for the article-template hero image (the LCP element on
 * template-rendered articles).
 *
 * The <link rel="preload"> emitted by the article page and the actual <img>
 * rendered by ArticleHeroSection MUST request byte-identical URLs with the
 * same srcset/sizes, or the browser preloads one variant and then downloads
 * another (wasted preload + double download). Both sides build their URLs
 * through these helpers to stay in sync. Query params are overwritten (not
 * appended) so a source URL that already carries w=/q= can't produce
 * conflicting duplicates — mirroring image-loader.ts's behavior.
 */

export const HERO_VARIANT_WIDTHS = [384, 640, 750, 828, 1080, 1200];

/** Default variant used as the plain src / preload href. */
export const HERO_DEFAULT_WIDTH = 800;

/**
 * The template hero renders edge-to-edge on mobile and caps at the article
 * column (~800px) on desktop. Used by both the preload's imageSizes and the
 * hero <img>'s sizes.
 */
export const TEMPLATE_HERO_SIZES = "(max-width: 767px) 100vw, 800px";

/**
 * A resized/compressed variant of a hero image. The publisher CDN (Gumlet)
 * resizes on the fly via query params and auto-serves AVIF/WebP per the
 * Accept header. Non-absolute URLs (local assets, data URIs) pass through
 * untouched via null so callers fall back to the raw src.
 */
export function buildHeroVariantUrl(src: string | undefined, width: number): string | null {
  if (!src || !/^https?:\/\//i.test(src)) return null;
  try {
    const url = new URL(src);
    url.searchParams.set('w', String(width));
    url.searchParams.set('q', '65');
    url.searchParams.set('auto', 'format,compress');
    url.searchParams.set('fit', 'max');
    return url.toString();
  } catch {
    return null;
  }
}

/** srcset covering HERO_VARIANT_WIDTHS, or undefined for non-CDN sources. */
export function buildHeroSrcSet(src: string | undefined): string | undefined {
  if (!src) return undefined;
  const entries = HERO_VARIANT_WIDTHS
    .map((w) => {
      const url = buildHeroVariantUrl(src, w);
      return url ? `${url} ${w}w` : null;
    })
    .filter(Boolean);
  return entries.length > 0 ? entries.join(', ') : undefined;
}
